import { normalizeTraceId, normalizeUseCase } from './normalizeLivenessParams';

/**
 * Body sent to persons-api `POST /persons/api/liveness`. Kept loose because
 * every field is optional server-side; only `person_uuid` is required.
 */
export type CreateLivenessSessionBody = {
    person_uuid: string;
    source?: string;
    manual_review_required?: boolean;
    preferred_vendor?: string;
    use_case?: string;
    trace_id?: string;
};

export type BuildCreateSessionBodyInput = {
    personUuid: string;
    isMobile: boolean;
    sourceId: string;
    searchParams: URLSearchParams;
    /**
     * Fields to force on top of whatever the launcher URL provided. Used by the
     * Incode→AWS fallback to set `preferred_vendor: 'aws'` and force
     * `manual_review_required: true` regardless of what the original URL asked for.
     */
    overrides?: Partial<CreateLivenessSessionBody>;
};

/**
 * Build the create-session request body from the launcher URL params plus the
 * component-owned inputs (`personUuid`, `sourceId`, `isMobile`). Single source
 * of truth so the initial call and the AWS fallback stay in lockstep.
 *
 * `use_case` and `trace_id` go through length caps (see normalizeLivenessParams)
 * to keep a garbled query param out of request bodies and logs.
 *
 * `manual_review_required` is only included when the URL literally says `true`
 * so we don't accidentally send `false` and mask a caller that meant to omit it.
 */
export function buildCreateSessionBody({
    personUuid,
    isMobile,
    sourceId,
    searchParams,
    overrides,
}: BuildCreateSessionBodyInput): CreateLivenessSessionBody {
    const manualReviewRequired = searchParams.get('manual_review_required') === 'true';
    const preferredVendor = searchParams.get('preferred_vendor');
    const useCase = normalizeUseCase(searchParams.get('use_case'));
    const traceId = normalizeTraceId(searchParams.get('trace_id'));

    return {
        person_uuid: personUuid,
        ...(!isMobile ? { source: sourceId } : {}),
        ...(manualReviewRequired ? { manual_review_required: true } : {}),
        ...(preferredVendor ? { preferred_vendor: preferredVendor } : {}),
        ...(useCase ? { use_case: useCase } : {}),
        ...(traceId ? { trace_id: traceId } : {}),
        ...(overrides ?? {}),
    };
}
